import React from "react";
import { graphql, Link } from "gatsby";

import {
    beautyPage,
    pageShell,
    hero,
    heroCopy,
    heroEyebrow,
    heroTitle,
    heroLead,
    heroCta,
    heroBenefits,
    heroBenefit,
    heroVisual,
    heroBadge,
    section,
    sectionHeading,
    seasonGrid,
    seasonCard,
    seasonHeader,
    seasonImage,
    seasonBody,
    seasonTagline,
    paletteLabel,
    palette,
    swatch,
    seasonLink,
    seasonDetails,
    spring,
    summer,
    autumn,
    winter,
    discoverySection,
    discoveryMain,
    discoverySteps,
    discoveryStep,
    stepIcon,
    stepIconImage,
    stepArrow,
    discoveryQuiz,
    quizButterfly,
    quizLink,
    looksGrid,
    lookCard,
    lookImage,
    lookSeason,
    lookBody,
    lookLink,
    finalCta,
    finalCopy,
    finalFeatures,
    finalFeature,
    finalFeatureIcon,
    finalPalette,
} from "./beauty-type.module.scss";

import heroImage from "../../assets/images/beauty/beauty-hero.jpg";
import springImage from "../../assets/images/beauty/color-analysis-result-spring.jpg";
import summerImage from "../../assets/images/beauty/reference/beauty-summer-reference.jpg";
import autumnImage from "../../assets/images/beauty/color-analysis-result-autumn.jpg";
import winterImage from "../../assets/images/beauty/color-analysis-result-winter.jpg";
import springLook from "../../assets/images/beauty/beauty-look-spring.jpg";
import summerLook from "../../assets/images/beauty/beauty-look-summer.jpg";
import autumnLook from "../../assets/images/beauty/beauty-look-autumn.jpg";
import winterLook from "../../assets/images/beauty/beauty-look-winter.jpg";
import paletteImage from "../../assets/images/beauty/beauty-palette-final.jpg";
import butterflyImage from "../../assets/images/beauty/reference/beauty-butterfly-reference.png";
import discoverySkinIcon from "../../assets/images/beauty/discovery/beauty-discovery-skin.svg";
import discoveryEyeIcon from "../../assets/images/beauty/discovery/beauty-discovery-eye.svg";
import discoveryHairIcon from "../../assets/images/beauty/discovery/beauty-discovery-hair.svg";
import discoveryFabricsIcon from "../../assets/images/beauty/discovery/beauty-discovery-fabrics.svg";
import discoveryIntuitionIcon from "../../assets/images/beauty/discovery/beauty-discovery-intuition.svg";
import loveArrow from "../../assets/icons/love-arrow.svg";
import FinalClockIcon from "../../assets/images/svg/clock.svg";
import FinalCheckIcon from "../../assets/images/svg/check.svg";
import FinalHeartIcon from "../../assets/images/svg/heart.svg";

import { IPageBase } from "../../models/page-base.model";
import { ISite } from "../../models/site.model";
import usePageMetadata from "../../hooks/use-page-metadata";
import MainLayout from "../../layouts/main-layout";
import StaticPageNav from "../organisms/static-page-nav";
import MinimalFooter from "../organisms/minimal-footer";

interface IBeautyProps extends IPageBase {
    readonly data: { site: ISite };
}

const seasons = [
    {
        name: "Wiosna",
        className: spring,
        image: springImage,
        tagline: "Ciepła, jasna, świeża",
        colors: ["#ff7b52", "#f28d60", "#f5aa78", "#f2c452", "#b8d07a", "#8dbf95", "#57afbf"],
        details: "Delikatna, świetlista skóra, jasne lub złociste włosy i ciepłe refleksy. Najlepiej służą jej jasne beże, łososiowy, écru, świeże zielenie i rozświetlone pastele.",
    },
    {
        name: "Lato",
        className: summer,
        image: summerImage,
        tagline: "Chłodna, jasna, stonowana",
        colors: ["#e98eaf", "#c7a6c9", "#b9c1dc", "#83a9c7", "#7f9f91", "#c8c4c2"],
        details: "Chłodna, subtelna uroda o zgaszonych włosach i delikatnej oprawie oczu. Najlepsze są pudrowe róże, błękity, fiolety, chłodne zielenie i złamana biel.",
    },
    {
        name: "Jesień",
        className: autumn,
        image: autumnImage,
        tagline: "Ciepła, głęboka, złota",
        colors: ["#6f8d13", "#d99508", "#ea7b08", "#c45510", "#9a3b16", "#6e2616", "#176c73"],
        details: "Ciepły, wyrazisty typ z rudymi lub złocistymi refleksami. Podkreślają go brązy, oliwka, khaki, złoto, krem, pomarańcz i pomidorowa czerwień.",
    },
    {
        name: "Zima",
        className: winter,
        image: winterImage,
        tagline: "Chłodna, kontrastowa, wyrazista",
        colors: ["#d50c69", "#425daa", "#38a7a9", "#304248", "#202326", "#101214", "#f3f3f0"],
        details: "Mocny kontrast między skórą, włosami i oczami. Zimie służą śnieżna biel, czerń, kobalt, fuksja, bordo, śliwka i butelkowa zieleń.",
    },
];

const discovery = [
    { icon: discoverySkinIcon, title: "Sprawdź odcień swojej skóry", text: "Czy jest ciepły, czy chłodny?" },
    { icon: discoveryEyeIcon, title: "Zwróć uwagę na kolor oczu", text: "Jakie kolory w nich dominują?" },
    { icon: discoveryHairIcon, title: "Oceń kolor swoich włosów", text: "Czy są jasne, ciemne, ciepłe czy chłodne?" },
    { icon: discoveryFabricsIcon, title: "Zrób test z tkaninami", text: "Które kolory dodają Ci blasku, a które gaszą?" },
    { icon: discoveryIntuitionIcon, title: "Poczuj, w czym czujesz się najlepiej", text: "Intuicja też wie swoje!" },
];

const looks = [
    { name: "Wiosna", className: spring, image: springLook, text: "Świeże, lekkie i pełne energii" },
    { name: "Lato", className: summer, image: summerLook, text: "Subtelne, eleganckie i romantyczne" },
    { name: "Jesień", className: autumn, image: autumnLook, text: "Ciepłe, przytulne i naturalne" },
    { name: "Zima", className: winter, image: winterLook, text: "Wyraziste, nowoczesne i pełne klasy" },
];

const BeautyType: React.FC<IBeautyProps> = ({ data, pageContext }) => {
    const { pageTitle, defaultStructuredData } = usePageMetadata(pageContext, data.site);

    return (
        <MainLayout
            className={beautyPage}
            HeaderComponent={null}
            footer={false}
            SEOProps={{ title: pageTitle, structuredData: defaultStructuredData }}
        >
            <StaticPageNav menuId="beauty-type-mobile-menu" />
            <main className={pageShell}>
                <section className={hero} aria-labelledby="beauty-title">
                    <div className={heroCopy}>
                        <p className={heroEyebrow}>Twój kolor. Twój blask.</p>
                        <h1 className={heroTitle} id="beauty-title">Odkryj swój<br /><em>typ urody</em><br />i dobierz kolory,<br />które Cię podkreślą.</h1>
                        <p className={heroLead}>Kolory mają moc! Mogą dodać Ci blasku, odmłodzić, podkreślić atuty i sprawić, że poczujesz się pewniej. Znajdź swoje idealne barwy!</p>
                        <Link className={heroCta} to="/analiza-kolorystyczna/">Wykonaj analizę kolorystyczną</Link>
                        <ul className={heroBenefits} aria-label="Korzyści analizy kolorystycznej">
                            {['Dopasowane do Ciebie', 'Prosta metoda', 'Natychmiastowe efekty'].map((item) => <li className={heroBenefit} key={item}><span>✓</span>{item}</li>)}
                        </ul>
                    </div>
                    <div className={heroVisual}>
                        <img src={heroImage} alt="Kobieta na różowym tle prezentująca ciepły typ urody" />
                        <p className={heroBadge}>Odpowiednie kolory to Twój sekret pewności siebie!</p>
                    </div>
                </section>

                <section className={section} aria-labelledby="seasons-title">
                    <header className={sectionHeading}><h2 id="seasons-title">Cztery typy urody, cztery palety kolorów</h2></header>
                    <div className={seasonGrid}>
                        {seasons.map((item) => (
                            <article className={`${seasonCard} ${item.className}`} id={item.name.toLowerCase()} key={item.name}>
                                <h3 className={seasonHeader}>{item.name}</h3>
                                <img className={seasonImage} src={item.image} alt={`Przykład typu urody: ${item.name}`} />
                                <div className={seasonBody}>
                                    <p className={seasonTagline}>{item.tagline}</p>
                                    <p className={paletteLabel}>Najlepsze kolory:</p>
                                    <div className={palette} aria-label={`Paleta: ${item.name}`}>{item.colors.map((color) => <span className={swatch} style={{ backgroundColor: color }} key={color} />)}</div>
                                    <details className={seasonDetails}><summary>Dowiedz się więcej</summary><p>{item.details}</p></details>
                                    <Link className={seasonLink} to="/analiza-kolorystyczna/">Sprawdź swój typ &nbsp; →</Link>
                                </div>
                            </article>
                        ))}
                    </div>
                </section>

                <section className={discoverySection} aria-labelledby="discovery-title">
                    <header className={sectionHeading}><h2 id="discovery-title">Jak odkryć swój typ urody?</h2></header>
                    <div className={discoveryMain}>
                        <div className={discoverySteps}>
                            {discovery.map((step, index) => (
                                <article className={discoveryStep} key={step.title}>
                                    <span className={stepIcon} aria-hidden="true"><img className={stepIconImage} src={step.icon} alt="" /></span>
                                    {index < discovery.length - 1 && <img className={stepArrow} src={loveArrow} alt="" />}
                                    <h3>{step.title}</h3><p>{step.text}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                    <aside className={discoveryQuiz}>
                        <img className={quizButterfly} src={butterflyImage} alt="" aria-hidden="true" />
                        <h2>Nie wiesz<br />od czego zacząć?</h2>
                        <p>Wykonaj nasz szybki quiz, a pomożemy Ci określić Twój typ urody!</p>
                        <Link className={quizLink} to="/analiza-kolorystyczna/">Rozpocznij quiz &nbsp; →</Link>
                    </aside>
                </section>

                <section className={section} aria-labelledby="looks-title">
                    <header className={sectionHeading}><h2 id="looks-title">Stylizacje dopasowane do Twojego typu urody</h2></header>
                    <div className={looksGrid}>
                        {looks.map((item) => (
                            <article className={`${lookCard} ${item.className}`} key={item.name}>
                                <img className={lookImage} src={item.image} alt={`Stylizacja dla typu urody ${item.name}`} />
                                <span className={lookSeason}>{item.name}</span>
                                <div className={lookBody}><p>{item.text}</p><Link className={lookLink} to="/wystylizuj-mnie/">Zobacz stylizacje &nbsp; →</Link></div>
                            </article>
                        ))}
                    </div>
                </section>

                <section className={finalCta} aria-labelledby="final-beauty-title">
                    <div className={finalCopy}>
                        <h2 id="final-beauty-title">Gotowa odkryć swoje kolory?</h2>
                        <p>Zrób analizę kolorystyczną i dowiedz się, jakie barwy wydobędą Twoje piękno i sprawią, że zawsze będziesz wyglądać olśniewająco.</p>
                        <Link className={heroCta} to="/analiza-kolorystyczna/">Wykonaj analizę kolorystyczną</Link>
                    </div>
                    <ul className={finalFeatures} aria-label="Zalety analizy">
                        <li className={finalFeature}><span className={finalFeatureIcon} aria-hidden="true"><FinalClockIcon /></span>Szybko</li>
                        <li className={finalFeature}><span className={finalFeatureIcon} aria-hidden="true"><FinalCheckIcon /></span>Prosto</li>
                        <li className={finalFeature}><span className={finalFeatureIcon} aria-hidden="true"><FinalHeartIcon /></span>Dopasowane<br />do Ciebie</li>
                    </ul>
                    <div className={finalPalette}><img src={paletteImage} alt="Wachlarz próbek kolorystycznych" /></div>
                </section>
            </main>
            <MinimalFooter />
        </MainLayout>
    );
};

export const query = graphql`
    query($language: String!) {
        locales: allLocale(filter: { language: { eq: $language } }) { edges { node { ns data language } } }
        site { ...siteFields }
    }
`;

export default BeautyType;
